import { Checkbox, ConfirmDialog, Label, SimpleCollapsibleContent, TextAreaFormField, TextAreaFormFieldProps } from "@/shared/components";
import { cn, convertToKebabCase } from "@/shared/utils";
import { useState } from "react";
import { FieldValues, PathValue } from "react-hook-form";

type EvaluatiePuntProps<TFormData extends FieldValues> = {
    label: string;
    className?: string;
    action?: React.ReactNode;
    onClear: () => void;
    defaultOpen?: boolean;
    readonly?: boolean;
} & Pick<TextAreaFormFieldProps<TFormData>, "form" | "name" | "placeholder">;

export function EvaluatiePunt<TFormData extends FieldValues>({
    form,
    name,
    label,
    placeholder,
    action,
    className,
    onClear,
    defaultOpen = false,
    readonly,
}: EvaluatiePuntProps<TFormData>) {
    const [isOpen, setIsOpen] = useState(defaultOpen);
    const [showConfirmDialog, setShowConfirmDialog] = useState(false);

    const handleConfirmClear = () => {
        form.setValue(name, "" as PathValue<TFormData, typeof name>);
        onClear();
        setIsOpen(false);
        setShowConfirmDialog(false);
    };

    const handleToggle = (e: React.MouseEvent<HTMLButtonElement>) => {
        const nextOpen = !isOpen;
        if (nextOpen) {
            setIsOpen(true);
        } else {
            const value = form.getValues(name) as string;
            if (value?.trim().length > 0) {
                setShowConfirmDialog(true);
            } else {
                setIsOpen(false);
            }
        }

        // prevent click from bubbling to the form's onChange; the checkbox only toggles the textarea, it doesn't represent a form field
        e.stopPropagation();
    };

    return (
        <div className={cn("grid gap-2", className)}>
            <div className="relative flex items-center gap-2">
                <Checkbox id={`checkbox-${name}`} checked={isOpen} onClick={readonly ? undefined : handleToggle} disabled={readonly} />
                <Label htmlFor={`checkbox-${name}`} className="text-content-default cursor-pointer font-normal peer-disabled:opacity-100">
                    {label}
                </Label>
            </div>
            <SimpleCollapsibleContent open={isOpen} preventFocusClipping className="pl-7">
                {action ? action : null}
                <TextAreaFormField
                    placeholder={placeholder}
                    form={form}
                    name={name}
                    tabIndex={isOpen ? 0 : -1}
                    readOnly={readonly}
                    data-testid={`evaluatiepunt-${convertToKebabCase(label)}`}
                />
            </SimpleCollapsibleContent>
            <ConfirmDialog
                isOpen={showConfirmDialog}
                onConfirm={handleConfirmClear}
                onCancel={() => setShowConfirmDialog(false)}
                title="Weet je het zeker?"
                confirmLabel="Verwijderen"
                cancelLabel="Annuleren"
                destructive
                isPending={false}
                error={null}
            >
                De tekst bij het evaluatiepunt '{label}' wordt verwijderd.
            </ConfirmDialog>
        </div>
    );
}
